import React, { useState, useMemo } from 'react';
import { CheckCircle, Info, BarChart3, Calculator } from 'lucide-react';

const CryptumCalculator = () => {
  const [activeTab, setActiveTab] = useState('calculator');
  const [scenario, setScenario] = useState('equilibrado');
  const [showKubernetesInfo, setShowKubernetesInfo] = useState(false);
  
  const [config, setConfig] = useState({
    region: 'br',
    services: {
      kubernetesTier: 'small', // small, medium, large
      databaseTier: 'small', // small, medium, large
      storageTier: 'small', // small, medium, large
      ai: true
    },
    ccbEmissions: 1000,
    transactions: [
      { id: 'ccb', name: 'Deploy CCB', enabled: true, quantity: 1000, gasUsage: 5600000 },
      { id: 'parcelas', name: 'Registro Parcelas', enabled: false, quantity: 2000, gasUsage: 3000000 },
      { id: 'compravenda', name: 'Compra/Venda', enabled: false, quantity: 5000, gasUsage: 160000 },
      { id: 'pagamento', name: 'Pagamentos', enabled: false, quantity: 10000, gasUsage: 106000 },
      { id: 'agent', name: 'Deploy Agent', enabled: false, quantity: 500, gasUsage: 7100000 }
    ]
  });

  // Cenários com todas as especificações
  const scenarios = {
    economico: {
      name: 'Baixo Custo',
      subtitle: 'PoC / Testes',
      badge: null,
      architecture: {
        totalNodes: 4,
        validators: 4,
        rpc: 0,
        deployment: '4 Nós Validadores'
      },
      machine: {
        type: 'e2-standard-8',
        vcpus: 8,
        ram: 32,
        description: 'Máquina de uso geral',
        disk: '500GB pd-balanced',
        diskNote: 'Disco balanceado (mais barato, latência maior)'
      },
      blockchain: {
        blockTime: 5,
        gasLimit: 300000000,
        consensus: 'QBFT (Finalidade imediata)'
      },
      performance: {
        deployAgent: { min: 2, max: 3 },
        deployCCB: { min: 3, max: 4 },
        registroParcelas: { min: 6, max: 8 },
        compraVenda: 150,
        pagamentos: { min: 200, max: 250 }
      },
      costs: {
        br: { vm: 270, disk: 80, infra: 28 },
        usd: { vm: 200, disk: 50, infra: 28 }
      },
      nodes: 4
    },
    equilibrado: {
      name: 'Equilibrada',
      subtitle: 'Produção Inicial',
      badge: 'RECOMENDADA',
      architecture: {
        totalNodes: 5,
        validators: 4,
        rpc: 1,
        deployment: '4 Validadores + 1 RPC'
      },
      machine: {
        type: 't2d-standard-8',
        vcpus: 8,
        ram: 32,
        description: 'AMD Milan (Excelente performance multi-thread)',
        disk: '500GB pd-ssd (Validadores) | 300GB pd-ssd (RPC)',
        diskNote: '500GB garante IOPS suficiente para não engasgar o consenso'
      },
      blockchain: {
        blockTime: 3,
        gasLimit: 300000000,
        consensus: 'QBFT (Finalidade imediata)'
      },
      performance: {
        deployAgent: { min: 8, max: 10 },
        deployCCB: { min: 10, max: 12 },
        registroParcelas: { min: 18, max: 22 },
        compraVenda: { min: 400, max: 500 },
        pagamentos: { min: 600, max: 800 }
      },
      costs: {
        br: { vm: 315, disk: 124.2, infra: 45 },
        usd: { vm: 222, disk: 78.2, infra: 45 }
      },
      nodes: 5
    },
    maxima: {
      name: 'Máxima Performance',
      subtitle: 'Alta Demanda - Escala Nacional',
      badge: null,
      architecture: {
        totalNodes: 7,
        validators: 5,
        rpc: 2,
        deployment: '5 Validadores + 2 RPC'
      },
      machine: {
        type: 'c2-standard-8',
        vcpus: 8,
        ram: 32,
        description: 'Intel Scalable 3.8GHz (Compute Optimized)',
        disk: '2x 375GB Local NVMe SSD (RAID 0 = 750GB)',
        diskNote: 'Latência zero para suportar block time de 2s'
      },
      blockchain: {
        blockTime: 2,
        gasLimit: 300000000,
        consensus: 'QBFT (Finalidade imediata)'
      },
      performance: {
        deployAgent: { min: 25, max: 30 },
        deployCCB: { min: 35, max: 40 },
        registroParcelas: { min: 60, max: 70 },
        compraVenda: 1200,
        pagamentos: '1800+'
      },
      costs: {
        br: { vm: 401, disk: 105, infra: 70 },
        usd: { vm: 298, disk: 66, infra: 70 }
      },
      nodes: 7
    }
  };

  const kubernetesTiers = {
    small: {
      name: 'Small',
      description: 'Piloto / PoC',
      specs: 'n2-standard-2 (2 vCPU, 8 GB RAM)',
      costs: { usd: 200, br: 1100 }
    },
    medium: {
      name: 'Medium',
      description: 'Produção',
      specs: 'n2-standard-4 (4 vCPU, 16 GB RAM)',
      costs: { usd: 400, br: 2200 }
    },
    large: {
      name: 'Large',
      description: 'Alta Escala',
      specs: 'n2-standard-8 (8 vCPU, 32 GB RAM)',
      costs: { usd: 800, br: 4400 }
    }
  };

  const databaseTiers = {
    small: {
      name: 'Small',
      description: 'MVP / Piloto',
      specs: '2 vCPU, 4 GB RAM, 50 GB Storage',
      qps: '~300 QPS',
      costs: { usd: 70, br: 385 }
    },
    medium: {
      name: 'Medium',
      description: 'Produção / SaaS',
      specs: '6 vCPU, 24 GB RAM, 350 GB Storage',
      qps: '~3.000 QPS',
      costs: { usd: 750, br: 4125 }
    },
    large: {
      name: 'Large',
      description: 'Enterprise / Infra crítica',
      specs: '32 vCPU, 128 GB RAM, 5 TB Storage',
      qps: '~30.000 QPS',
      costs: { usd: 8000, br: 44000 }
    }
  };

  const storageTiers = {
    small: {
      name: 'Small',
      description: 'Básico',
      size: '100 GB',
      costs: { usd: 20, br: 110 }
    },
    medium: {
      name: 'Medium',
      description: 'Padrão',
      size: '500 GB',
      costs: { usd: 100, br: 550 }
    },
    large: {
      name: 'Large',
      description: 'Extensivo',
      size: '2 TB',
      costs: { usd: 400, br: 2200 }
    }
  };

  const serviceCosts = {
    ai: {
      name: 'IA (Vertex AI)',
      description: 'Custo fixo de infraestrutura (endpoints, orquestração, OCR, logs)',
      fixedCosts: { usd: 1700, br: 8500 },
      variableCostPerEmission: { usd: 0.36, br: 1.8 }
    }
  };

  // Cálculo dos custos e performance
  const results = useMemo(() => {
    const sc = scenarios[scenario];
    const currency = config.region === 'br' ? 'br' : 'usd';
    const currencySymbol = config.region === 'br' ? 'R$' : '$';

    // Infraestrutura Blockchain
    const vmCost = sc.costs[currency].vm * sc.nodes;
    const diskCost = sc.costs[currency].disk * sc.nodes;
    const infraCost = sc.costs[currency].infra;
    const blockchainTotal = vmCost + diskCost + infraCost;

    // Serviços Cloud
    let servicesTotal = 0;
    let servicesList = [];

    if (config.services.kubernetesTier) {
      const kubeCost = kubernetesTiers[config.services.kubernetesTier].costs[currency];
      servicesTotal += kubeCost;
      servicesList.push({
        name: `Kubernetes (${kubernetesTiers[config.services.kubernetesTier].name})`,
        cost: kubeCost
      });
    }

    if (config.services.databaseTier) {
      const dbCost = databaseTiers[config.services.databaseTier].costs[currency];
      servicesTotal += dbCost;
      servicesList.push({
        name: `MySQL Database (${databaseTiers[config.services.databaseTier].name})`,
        cost: dbCost
      });
    }

    if (config.services.storageTier) {
      const storageCost = storageTiers[config.services.storageTier].costs[currency];
      servicesTotal += storageCost;
      servicesList.push({
        name: `Cloud Storage (${storageTiers[config.services.storageTier].name})`,
        cost: storageCost
      });
    }

    // Custos de IA (relacionados a Deploy CCB)
    let aiFixedCost = 0;
    let aiVariableCost = 0;
    let aiTotalCost = 0;

    if (config.services.ai) {
      const ccbTransaction = config.transactions.find(t => t.id === 'ccb');
      const ccbEmissions = ccbTransaction && ccbTransaction.enabled ? ccbTransaction.quantity : 0;

      aiFixedCost = serviceCosts.ai.fixedCosts[currency];
      aiVariableCost = ccbEmissions * serviceCosts.ai.variableCostPerEmission[currency];
      aiTotalCost = aiFixedCost + aiVariableCost;
    }

    // Total
    const total = blockchainTotal + servicesTotal + aiTotalCost;

    // Cálculo de TPS por tipo de transação
    const tpsByTransaction = config.transactions.map(tx => {
      if (!tx.enabled) return null;

      const tpsTheoretical = sc.blockchain.gasLimit / tx.gasUsage / sc.blockchain.blockTime;
      
      let tpsReal;
      switch (tx.id) {
        case 'agent':
          tpsReal = sc.performance.deployAgent;
          break;
        case 'ccb':
          tpsReal = sc.performance.deployCCB;
          break;
        case 'parcelas':
          tpsReal = sc.performance.registroParcelas;
          break;
        case 'compravenda':
          tpsReal = sc.performance.compraVenda;
          break;
        case 'pagamento':
          tpsReal = sc.performance.pagamentos;
          break;
        default:
          tpsReal = tpsTheoretical * 0.7;
      }

      return {
        ...tx,
        tpsTheoretical: Math.round(tpsTheoretical * 10) / 10,
        tpsReal: tpsReal,
        capacityPerMonth: typeof tpsReal === 'object' 
          ? Math.round((tpsReal.max * 86400 * 30))
          : Math.round((tpsReal * 86400 * 30)),
        utilizationPercent: typeof tpsReal === 'object'
          ? (tx.quantity / (tpsReal.max * 86400 * 30)) * 100
          : (tx.quantity / (tpsReal * 86400 * 30)) * 100
      };
    }).filter(Boolean);

    // TPS gargalo
    const bottleneckTPS = tpsByTransaction.length > 0
      ? Math.min(...tpsByTransaction.map(t => 
          typeof t.tpsReal === 'object' ? t.tpsReal.min : (typeof t.tpsReal === 'string' ? 1800 : t.tpsReal)
        ))
      : 0;

    // Custo por emissão CCB
    const ccbTransaction = config.transactions.find(t => t.id === 'ccb');
    const ccbEmissions = ccbTransaction && ccbTransaction.enabled ? ccbTransaction.quantity : 0;
    const costPerCCBEmission = ccbEmissions > 0 ? total / ccbEmissions : 0;

    return {
      blockchain: {
        vm: vmCost,
        disk: diskCost,
        infra: infraCost,
        total: blockchainTotal
      },
      services: {
        total: servicesTotal,
        list: servicesList
      },
      ai: {
        fixed: aiFixedCost,
        variable: aiVariableCost,
        total: aiTotalCost,
        perEmission: aiVariableCost > 0 ? aiVariableCost / ccbEmissions : 0
      },
      total,
      currencySymbol,
      tpsByTransaction,
      bottleneckTPS,
      ccbEmissions,
      costPerCCBEmission,
      scenarioSpecs: sc
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, config]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f1f5f9',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color: '#0f172a'
    }}>
      
      {/* Header Enterprise */}
      <div style={{
        background: '#ffffff',
        borderBottom: '2px solid #e2e8f0',
        padding: '1.5rem 0',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <h1 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#0f172a',
              margin: 0,
              marginBottom: '0.5rem',
              letterSpacing: '-0.01em'
            }}>
              Calculadora Cryptum
            </h1>
            <p style={{
              fontSize: '0.95rem',
              color: '#64748b',
              margin: 0,
              fontWeight: '500'
            }}>
              Custos Infraestrutura Blockchain
            </p>
          </div>

          {/* Product Pills */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: '#ffffff',
              borderRadius: '6px',
              fontSize: '0.8rem',
              fontWeight: '600',
              letterSpacing: '0.02em'
            }}>
              Tokenização CCB On-Chain
            </div>
            <div style={{
              padding: '0.5rem 1rem',
              background: '#e2e8f0',
              color: '#94a3b8',
              borderRadius: '6px',
              fontSize: '0.8rem',
              fontWeight: '600',
              letterSpacing: '0.02em'
            }}>
              Tokenização Debênture On-Chain
            </div>
            <div style={{
              padding: '0.5rem 1rem',
              background: '#e2e8f0',
              color: '#94a3b8',
              borderRadius: '6px',
              fontSize: '0.8rem',
              fontWeight: '600',
              letterSpacing: '0.02em'
            }}>
              Tokenização CRI On-Chain
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1.5rem',
            borderBottom: '2px solid #e2e8f0'
          }}>
            {[
              { id: 'calculator', label: 'Calculadora', icon: Calculator },
              { id: 'analysis', label: 'Análises', icon: BarChart3 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '3px solid #3b82f6' : '3px solid transparent',
                    padding: '0.75rem 1.25rem',
                    color: activeTab === tab.id ? '#3b82f6' : '#64748b',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '-2px'
                  }}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem' }}>
        
        {activeTab === 'calculator' && (
          <>
            {/* Section 1: Escolha do Cenário */}
            <div style={{
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              padding: '2rem',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'inline-block',
                  background: '#3b82f6',
                  color: '#ffffff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  marginBottom: '0.75rem'
                }}>
                  PASSO 1
                </div>
                <h2 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Escolha do Cenário
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                  Selecione a configuração de infraestrutura blockchain
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '1.25rem'
              }}>
                {Object.entries(scenarios).map(([key, sc]) => (
                  <button
                    key={key}
                    onClick={() => setScenario(key)}
                    style={{
                      background: scenario === key ? '#f8fafc' : '#ffffff',
                      border: scenario === key ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left',
                      position: 'relative'
                    }}
                  >
                    {scenario === key && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircle size={16} color="white" />
                      </div>
                    )}

                    {sc.badge && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        left: '1rem',
                        background: '#10b981',
                        color: '#ffffff',
                        padding: '0.25rem 0.6rem',
                        borderRadius: '4px',
                        fontSize: '0.65rem',
                        fontWeight: '700',
                        letterSpacing: '0.05em'
                      }}>
                        {sc.badge}
                      </div>
                    )}
                    
                    <div style={{ marginTop: sc.badge ? '1.5rem' : '0' }}>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#0f172a',
                        marginBottom: '0.25rem'
                      }}>
                        {sc.name}
                      </h3>
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                        marginBottom: '1rem'
                      }}>
                        {sc.subtitle}
                      </div>

                      <div style={{
                        background: '#f8fafc',
                        padding: '0.9rem',
                        borderRadius: '6px',
                        marginBottom: '0.9rem',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Arquitetura
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#0f172a', marginBottom: '0.4rem', fontWeight: '600' }}>
                          {sc.architecture.deployment}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {sc.architecture.validators} Validadores
                          {sc.architecture.rpc > 0 && ` + ${sc.architecture.rpc} RPC`}
                          {' '}({sc.architecture.totalNodes} nós total)
                        </div>
                      </div>

                      <div style={{
                        background: '#f8fafc',
                        padding: '0.9rem',
                        borderRadius: '6px',
                        marginBottom: '0.9rem',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Hardware
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#0f172a', marginBottom: '0.3rem', fontWeight: '600' }}>
                          {sc.machine.type}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.3rem' }}>
                          {sc.machine.vcpus} vCPU, {sc.machine.ram} GB RAM
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
                          {sc.machine.description}
                        </div>
                      </div>

                      <div style={{
                        background: '#f8fafc',
                        padding: '0.9rem',
                        borderRadius: '6px',
                        marginBottom: '0.9rem',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Disco
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#0f172a', marginBottom: '0.3rem', fontWeight: '600' }}>
                          {sc.machine.disk}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
                          {sc.machine.diskNote}
                        </div>
                      </div>

                      <div style={{
                        background: '#f8fafc',
                        padding: '0.9rem',
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.6rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Blockchain
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                          <div>
                            <div style={{ color: '#64748b' }}>Block Time</div>
                            <div style={{ color: '#0f172a', fontWeight: '600' }}>{sc.blockchain.blockTime}s</div>
                          </div>
                          <div>
                            <div style={{ color: '#64748b' }}>Gas Limit</div>
                            <div style={{ color: '#0f172a', fontWeight: '600' }}>{(sc.blockchain.gasLimit / 1000000).toFixed(0)}M</div>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.5rem' }}>
                          {sc.blockchain.consensus}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Section 2: Configurações */}
            <div style={{
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              padding: '2rem',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'inline-block',
                  background: '#10b981',
                  color: '#ffffff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  marginBottom: '0.75rem'
                }}>
                  PASSO 2
                </div>
                <h2 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Configurações
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                  Ajuste região, serviços cloud e tipos de transação
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1.5rem'
              }}>
                {/* Região */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: '#334155',
                    marginBottom: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Região
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {[
                      { key: 'usd', label: 'EUA', flag: '🇺🇸', sub: 'Iowa' },
                      { key: 'br', label: 'Brasil', flag: '🇧🇷', sub: 'São Paulo' }
                    ].map(region => (
                      <button
                        key={region.key}
                        onClick={() => setConfig({ ...config, region: region.key })}
                        style={{
                          background: config.region === region.key ? '#eff6ff' : '#ffffff',
                          border: config.region === region.key ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                          borderRadius: '6px',
                          padding: '0.75rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>{region.flag}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.1rem' }}>
                          {region.label}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          {region.sub}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Kubernetes */}
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <label style={{
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      color: '#334155',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Kubernetes
                    </label>
                    <div 
                      style={{ 
                        position: 'relative',
                        display: 'inline-flex',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={() => setShowKubernetesInfo(true)}
                      onMouseLeave={() => setShowKubernetesInfo(false)}
                      onClick={() => setShowKubernetesInfo(!showKubernetesInfo)}
                    >
                      <Info size={16} color="#64748b" />
                      {showKubernetesInfo && (
                        <div style={{
                          position: 'absolute',
                          top: '24px',
                          left: '-150px',
                          width: '320px',
                          background: '#0f172a',
                          color: '#ffffff',
                          padding: '0.75rem',
                          borderRadius: '6px',
                          fontSize: '0.75rem',
                          lineHeight: '1.5',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                          border: '1px solid #334155'
                        }}>
                          Mesmo em cenários de escala on-chain, o custo de Kubernetes é relativamente estável. O que escala o cluster são integrações, usuários e requisitos de disponibilidade - não TPS.
                        </div>
                      )}
                    </div>
                  </div>
                  <select
                    value={config.services.kubernetesTier}
                    onChange={(e) => setConfig({
                      ...config,
                      services: { ...config.services, kubernetesTier: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#0f172a',
                      cursor: 'pointer',
                      marginBottom: '0.5rem'
                    }}
                  >
                    {Object.entries(kubernetesTiers).map(([key, tier]) => (
                      <option key={key} value={key}>
                        {tier.name} - {tier.description} ({tier.specs})
                      </option>
                    ))}
                  </select>
                  <div style={{
                    background: '#f8fafc',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Componentes:
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#0f172a', lineHeight: '1.6' }}>
                      • Backend API<br />
                      • Dashboard Web<br />
                      • Cronjob Scheduler<br />
                      • Order Consumer<br />
                      • Agent AI<br />
                      • Agent OCR<br />
                      • RabbitMQ Message Broker<br />
                      • Bridge Service
                    </div>
                    <div style={{
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid #e2e8f0',
                      fontSize: '0.75rem',
                      color: '#10b981',
                      fontWeight: '600'
                    }}>
                      {results.currencySymbol} {kubernetesTiers[config.services.kubernetesTier].costs[config.region]}/mês
                    </div>
                  </div>
                </div>

                {/* Database */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: '#334155',
                    marginBottom: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Database (MySQL)
                  </label>
                  <select
                    value={config.services.databaseTier}
                    onChange={(e) => setConfig({
                      ...config,
                      services: { ...config.services, databaseTier: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#0f172a',
                      cursor: 'pointer'
                    }}
                  >
                    {Object.entries(databaseTiers).map(([key, tier]) => (
                      <option key={key} value={key}>
                        {tier.name} - {tier.description} ({tier.specs})
                      </option>
                    ))}
                  </select>
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#64748b',
                    background: '#f8fafc',
                    padding: '0.5rem',
                    borderRadius: '4px'
                  }}>
                    {databaseTiers[config.services.databaseTier].qps} • {results.currencySymbol} {databaseTiers[config.services.databaseTier].costs[config.region]}/mês
                  </div>
                </div>

                {/* Cloud Storage */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    color: '#334155',
                    marginBottom: '0.6rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Cloud Storage
                  </label>
                  <select
                    value={config.services.storageTier}
                    onChange={(e) => setConfig({
                      ...config,
                      services: { ...config.services, storageTier: e.target.value }
                    })}
                    style={{
                      width: '100%',
                      background: '#ffffff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#0f172a',
                      cursor: 'pointer'
                    }}
                  >
                    {Object.entries(storageTiers).map(([key, tier]) => (
                      <option key={key} value={key}>
                        {tier.name} - {tier.description} ({tier.size})
                      </option>
                    ))}
                  </select>
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: '#64748b',
                    background: '#f8fafc',
                    padding: '0.5rem',
                    borderRadius: '4px'
                  }}>
                    {storageTiers[config.services.storageTier].size} • {results.currencySymbol} {storageTiers[config.services.storageTier].costs[config.region]}/mês
                  </div>
                </div>

                {/* IA */}
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.9rem',
                    background: config.services.ai ? '#fef3c7' : '#f8fafc',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: config.services.ai ? '2px solid #f59e0b' : '2px solid #e2e8f0',
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="checkbox"
                      checked={config.services.ai}
                      onChange={(e) => setConfig({
                        ...config,
                        services: { ...config.services, ai: e.target.checked }
                      })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#f59e0b',
                        marginTop: '0.1rem'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', fontSize: '0.8rem', color: '#0f172a', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        IA (Vertex AI)
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.4rem', lineHeight: '1.4' }}>
                        {serviceCosts.ai.description}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#0f172a', marginBottom: '0.25rem' }}>
                        <strong>Fixo:</strong> {results.currencySymbol} {serviceCosts.ai.fixedCosts[config.region]}/mês
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                        <strong>Variável:</strong> {results.currencySymbol} {serviceCosts.ai.variableCostPerEmission[config.region]} por emissão CCB
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Section 3: Tipos de Transação */}
            <div style={{
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              padding: '2rem',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'inline-block',
                  background: '#f59e0b',
                  color: '#ffffff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  marginBottom: '0.75rem'
                }}>
                  PASSO 3
                </div>
                <h2 style={{
                  fontSize: '1.3rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Tipos de Transação
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
                  Selecione os tipos de transação e quantidades mensais (o TPS da mais baixa será o gargalo)
                </p>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem'
              }}>
                {config.transactions.map((tx, idx) => (
                  <div
                    key={tx.id}
                    style={{
                      background: tx.enabled ? '#f0fdf4' : '#f8fafc',
                      border: tx.enabled ? '2px solid #10b981' : '2px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '1rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      marginBottom: '0.75rem',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={tx.enabled}
                        onChange={(e) => {
                          const newTransactions = [...config.transactions];
                          newTransactions[idx].enabled = e.target.checked;
                          setConfig({ ...config, transactions: newTransactions });
                        }}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#10b981'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#0f172a' }}>
                          {tx.name}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          ~{(tx.gasUsage / 1000000).toFixed(1)}M gas fee
                        </div>
                      </div>
                    </label>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.7rem',
                        color: '#64748b',
                        marginBottom: '0.4rem',
                        fontWeight: '600'
                      }}>
                        Quantidade Mensal
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={tx.quantity}
                        onChange={(e) => {
                          const newTransactions = [...config.transactions];
                          newTransactions[idx].quantity = parseInt(e.target.value) || 0;
                          setConfig({ ...config, transactions: newTransactions });
                        }}
                        disabled={!tx.enabled}
                        style={{
                          width: '100%',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          padding: '0.6rem',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#0f172a',
                          opacity: tx.enabled ? 1 : 0.5
                        }}
                      />
                    </div>

                    {tx.id === 'ccb' && tx.enabled && config.services.ai && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.6rem',
                        background: '#fef3c7',
                        borderRadius: '4px',
                        border: '1px solid #fde68a',
                        fontSize: '0.7rem',
                        color: '#92400e'
                      }}>
                        💡 Custo IA: {results.currencySymbol} {(tx.quantity * serviceCosts.ai.variableCostPerEmission[config.region]).toFixed(2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Resultado Final */}
            <div style={{
              background: '#0f172a',
              borderRadius: '8px',
              padding: '2.5rem',
              border: '1px solid #1e293b',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  display: 'inline-block',
                  background: '#3b82f6',
                  color: '#ffffff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  letterSpacing: '0.05em',
                  marginBottom: '0.75rem'
                }}>
                  RESULTADO
                </div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  margin: 0,
                  marginBottom: '0.25rem'
                }}>
                  Custos e Performance
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0 }}>
                  Resumo financeiro e capacidade da infraestrutura
                </p>
              </div>

              {/* Custo Total */}
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                borderRadius: '8px',
                padding: '2rem',
                marginBottom: '2rem',
                border: '1px solid #334155'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                    Custo Mensal Total
                  </div>
                  <div style={{
                    fontSize: '4rem',
                    fontWeight: '900',
                    color: '#10b981',
                    lineHeight: '1',
                    letterSpacing: '-0.02em'
                  }}>
                    {results.currencySymbol} {results.total.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                  {results.ccbEmissions > 0 && (
                    <div style={{ fontSize: '0.9rem', color: '#6ee7b7', marginTop: '0.75rem', fontWeight: '600' }}>
                      {results.currencySymbol} {results.costPerCCBEmission.toFixed(2)} por emissão CCB
                    </div>
                  )}
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '1rem'
                }}>
                  <div style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    padding: '1rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(148, 163, 184, 0.2)'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.4rem' }}>
                      Infraestrutura Blockchain
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#60a5fa' }}>
                      {results.currencySymbol} {results.blockchain.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  <div style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    padding: '1rem',
                    borderRadius: '6px',
                    border: '1px solid rgba(148, 163, 184, 0.2)'
                  }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.4rem' }}>
                      Serviços Cloud
                    </div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#60a5fa' }}>
                      {results.currencySymbol} {results.services.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  {results.ai.total > 0 && (
                    <div style={{
                      background: 'rgba(15, 23, 42, 0.5)',
                      padding: '1rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(148, 163, 184, 0.2)'
                    }}>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.4rem' }}>
                        IA (Fixo + Variável)
                      </div>
                      <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#fbbf24' }}>
                        {results.currencySymbol} {results.ai.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* TPS por Tipo de Transação */}
              <div style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                borderRadius: '8px',
                padding: '2rem',
                border: '1px solid #334155'
              }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: '600' }}>
                    Performance por Tipo de Transação
                  </div>
                  {results.bottleneckTPS > 0 && (
                    <div style={{
                      display: 'inline-block',
                      background: '#ef4444',
                      color: '#ffffff',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      TPS Gargalo: {results.bottleneckTPS} TPS
                    </div>
                  )}
                </div>

                {results.tpsByTransaction.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#64748b',
                    fontSize: '0.9rem'
                  }}>
                    Nenhuma transação selecionada. Selecione pelo menos um tipo de transação no Passo 3.
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1rem'
                  }}>
                    {results.tpsByTransaction.map((tx, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: 'rgba(15, 23, 42, 0.5)',
                          padding: '1.25rem',
                          borderRadius: '6px',
                          border: '1px solid rgba(148, 163, 184, 0.2)'
                        }}
                      >
                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#f1f5f9', marginBottom: '0.25rem' }}>
                            {tx.name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {tx.quantity.toLocaleString()} tx/mês
                          </div>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '0.3rem' }}>
                            TPS Real (Transações/segundo)
                          </div>
                          <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#10b981' }}>
                            {typeof tx.tpsReal === 'object' 
                              ? `${tx.tpsReal.min}-${tx.tpsReal.max}`
                              : tx.tpsReal
                            } TPS
                          </div>
                        </div>

                        <div style={{
                          padding: '0.6rem',
                          background: 'rgba(15, 23, 42, 0.5)',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          marginBottom: '0.75rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                            <span style={{ color: '#94a3b8' }}>TPS Teórico:</span>
                            <span style={{ color: '#cbd5e1', fontWeight: '600' }}>{tx.tpsTheoretical}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#94a3b8' }}>Capacidade/mês:</span>
                            <span style={{ color: '#cbd5e1', fontWeight: '600' }}>{tx.capacityPerMonth.toLocaleString()}</span>
                          </div>
                        </div>

                        <div style={{
                          width: '100%',
                          height: '6px',
                          background: 'rgba(51, 65, 85, 0.5)',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{
                            width: `${Math.min(tx.utilizationPercent, 100)}%`,
                            height: '100%',
                            background: tx.utilizationPercent > 100
                              ? '#ef4444'
                              : tx.utilizationPercent > 80
                              ? '#fbbf24'
                              : '#10b981',
                            transition: 'all 0.5s ease'
                          }} />
                        </div>

                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', textAlign: 'center' }}>
                          {tx.utilizationPercent > 100 && (
                            <span style={{ color: '#fca5a5', fontWeight: '600' }}>⚠️ Sobrecarga ({tx.utilizationPercent.toFixed(0)}%)</span>
                          )}
                          {tx.utilizationPercent <= 100 && tx.utilizationPercent > 80 && (
                            <span style={{ color: '#fde68a', fontWeight: '600' }}>Alta utilização ({tx.utilizationPercent.toFixed(0)}%)</span>
                          )}
                          {tx.utilizationPercent <= 80 && (
                            <span style={{ color: '#6ee7b7', fontWeight: '600' }}>Utilização: {tx.utilizationPercent.toFixed(0)}%</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'analysis' && (
          <div style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            padding: '2rem',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#0f172a',
              marginBottom: '1.5rem'
            }}>
              Análises e Comparações
            </h2>

            {/* Breakdown Detalhado */}
            <div style={{
              background: '#f8fafc',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#0f172a',
                marginBottom: '1rem'
              }}>
                Breakdown Detalhado de Custos
              </h3>

              <div style={{
                background: '#ffffff',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Infraestrutura Blockchain
                </div>
                <div style={{ fontSize: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b' }}>VMs ({results.scenarioSpecs.nodes}x {results.scenarioSpecs.machine.type})</span>
                    <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {results.blockchain.vm.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b' }}>Discos</span>
                    <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {results.blockchain.disk.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                    <span style={{ color: '#64748b' }}>Infra Extra (LB + Monitoring)</span>
                    <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {results.blockchain.infra.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.5rem' }}>
                    <span style={{ fontWeight: '700', color: '#0f172a' }}>Subtotal Blockchain</span>
                    <span style={{ fontWeight: '700', color: '#3b82f6', fontSize: '1.1rem' }}>{results.currencySymbol} {results.blockchain.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div style={{
                background: '#ffffff',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Serviços Cloud
                </div>
                <div style={{ fontSize: '0.9rem' }}>
                  {results.services.list.map((service, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>{service.name}</span>
                      <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {service.cost.toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.5rem' }}>
                    <span style={{ fontWeight: '700', color: '#0f172a' }}>Subtotal Serviços</span>
                    <span style={{ fontWeight: '700', color: '#10b981', fontSize: '1.1rem' }}>{results.currencySymbol} {results.services.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {results.ai.total > 0 && (
                <div style={{
                  background: '#ffffff',
                  borderRadius: '6px',
                  padding: '1rem'
                }}>
                  <div style={{ fontWeight: '700', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Custos de IA
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Infraestrutura Fixa</span>
                      <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {results.ai.fixed.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                      <span style={{ color: '#64748b' }}>Variável ({results.ccbEmissions} emissões CCB)</span>
                      <span style={{ fontWeight: '600', color: '#0f172a' }}>{results.currencySymbol} {results.ai.variable.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.5rem' }}>
                      <span style={{ fontWeight: '700', color: '#0f172a' }}>Subtotal IA</span>
                      <span style={{ fontWeight: '700', color: '#f59e0b', fontSize: '1.1rem' }}>{results.currencySymbol} {results.ai.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                borderRadius: '6px',
                border: '2px solid #3b82f6'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '700', fontSize: '1.2rem', color: '#0f172a' }}>TOTAL MENSAL</span>
                  <span style={{ fontWeight: '900', fontSize: '1.8rem', color: '#3b82f6' }}>
                    {results.currencySymbol} {results.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Distribuição Visual */}
            <div style={{
              background: '#f8fafc',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#0f172a',
                marginBottom: '1rem'
              }}>
                Distribuição de Custos
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div style={{
                  background: '#ffffff',
                  padding: '1.25rem',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                    Blockchain
                  </div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#3b82f6', marginBottom: '0.25rem' }}>
                    {((results.blockchain.total / results.total) * 100).toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {results.currencySymbol} {results.blockchain.total.toFixed(2)}
                  </div>
                </div>

                <div style={{
                  background: '#ffffff',
                  padding: '1.25rem',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                    Serviços
                  </div>
                  <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#10b981', marginBottom: '0.25rem' }}>
                    {((results.services.total / results.total) * 100).toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    {results.currencySymbol} {results.services.total.toFixed(2)}
                  </div>
                </div>

                {results.ai.total > 0 && (
                  <div style={{
                    background: '#ffffff',
                    padding: '1.25rem',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600' }}>
                      IA
                    </div>
                    <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#f59e0b', marginBottom: '0.25rem' }}>
                      {((results.ai.total / results.total) * 100).toFixed(0)}%
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {results.currencySymbol} {results.ai.total.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptumCalculator;